
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/context/AuthContext";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle, Shield } from "lucide-react";

const AssignIntegrationsManagerRole = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const { user, addRole, hasRole, roles } = useAuth();
  const { toast } = useToast();
  
  const hasIntegrationsManagerRole = hasRole('integrations_manager');

  useEffect(() => {
    // Check if the current user is the authorized admin
    setIsAuthorized(user?.email === 'thiagogaravelo@gmail.com');
  }, [user]);

  const handleAssignRole = async () => {
    if (!user) {
      toast({
        title: "Erro",
        description: "Você precisa estar logado para atribuir funções",
        variant: "destructive",
      });
      return;
    }
    
    // Don't attempt to add the role if the user already has it
    if (hasIntegrationsManagerRole) {
      toast({
        title: "Função já Atribuída",
        description: "Você já possui a função de Gerente de Integrações",
      });
      return;
    }
    
    // Only proceed if the user is authorized
    if (!isAuthorized) {
      toast({
        title: "Acesso Negado",
        description: "Apenas o administrador autorizado pode atribuir funções.",
        variant: "destructive",
      });
      return;
    }
    
    setIsLoading(true);
    
    try {
      if (!addRole) {
        throw new Error("Função addRole não disponível");
      }
      
      const success = await addRole(user.id, 'integrations_manager');
      
      if (success) {
        toast({
          title: "Sucesso",
          description: "Função de Gerente de Integrações atribuída com sucesso",
        });
      }
    } catch (error: any) {
      console.error("Erro ao atribuir função:", error);
      toast({
        title: "Erro",
        description: error.message || "Não foi possível atribuir a função de Gerente de Integrações",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  if (!isAuthorized && !hasIntegrationsManagerRole) {
    return (
      <Alert variant="destructive" className="mb-4">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Acesso Restrito</AlertTitle>
        <AlertDescription>
          Apenas o administrador autorizado pode gerenciar funções de usuário.
        </AlertDescription>
      </Alert>
    );
  }
  
  return (
    <div className="space-y-4">
      <div className="flex items-center space-x-2">
        <span className="text-sm">Funções atuais:</span>
        <div className="flex gap-1 flex-wrap">
          {roles?.length ? (
            roles.map(role => (
              <span 
                key={role} 
                className="text-xs px-2 py-1 rounded-full bg-gray-100"
              >
                {role === "admin" ? "Administrador" : 
                role === "marketing" ? "Marketing" : 
                role === "support" ? "Suporte" : 
                role === "integrations_manager" ? "Gerente de Integrações" : role}
              </span>
            ))
          ) : (
            <span className="text-xs text-gray-500">Nenhuma função atribuída</span>
          )}
        </div>
      </div>
      
      {(isAuthorized || hasIntegrationsManagerRole) && (
        <Button 
          onClick={handleAssignRole} 
          disabled={isLoading || hasIntegrationsManagerRole || !isAuthorized}
          className={hasIntegrationsManagerRole ? "bg-green-100 text-green-800 hover:bg-green-100 cursor-default" : ""}
        >
          {isLoading ? "Atribuindo..." : 
          hasIntegrationsManagerRole ? "Função já Atribuída" : 
          "Atribuir Função de Gerente de Integrações"}
        </Button>
      )}
    </div>
  );
};

export default AssignIntegrationsManagerRole;
